import React, { useEffect, useState } from 'react';
import { Button, Typography, Result } from 'antd';
import { CheckCircle, RotateCcw } from 'lucide-react';

const { Title, Text } = Typography;

interface FeedbackThankYouProps {
  name: string;
  onReset: () => void;
}

const FeedbackThankYou: React.FC<FeedbackThankYouProps> = ({ name, onReset }) => {
  const [countdown, setCountdown] = useState(30);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          onReset();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onReset]);

  return (
    <div className="h-full flex flex-col max-w-2xl mx-auto text-center">
      <div className="flex-1 flex flex-col justify-center">
        <Result
          icon={
            <div className="mb-4">
              <div className="text-green-500 mb-3">
                <CheckCircle size={60} className="mx-auto" />
              </div>
              <div className="text-4xl mb-3">🎉</div>
            </div>
          }
          title={
            <Title level={3} className="text-gray-800 mb-2">
              Thank You, {name}!
            </Title>
          }
          subTitle={
            <div className="space-y-2">
              <Text className="text-base text-gray-600 block">
                Your feedback has been successfully submitted and is very important to us.
              </Text>
              <Text className="text-base text-gray-600 block">
                We appreciate you taking the time to share your experience with us.
              </Text>
            </div>
          }
        />

        <div className="mt-4 p-4 bg-green-50 rounded-lg">
          <Text className="text-base text-green-700 block mb-1">
            ✅ Feedback Submitted Successfully
          </Text>
          <Text className="text-sm text-gray-600">
            Your feedback will help us improve our services and better serve our customers.
          </Text>
        </div>

        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <Text className="text-blue-600 block mb-1">
            This form will automatically reset in{' '}
            <span className="font-bold text-lg">{countdown}</span> seconds
          </Text>
          <Text className="text-blue-500 text-xs">
            Ready for the next person to provide feedback
          </Text>
        </div>
      </div>

      <div className="mt-4">
        <Button
          type="primary"
          size="large"
          onClick={onReset}
          icon={<RotateCcw size={18} />}
          className="px-8 py-2 h-auto rounded-lg bg-blue-600 hover:bg-blue-700 border-blue-600 hover:border-blue-700"
          style={{ fontSize: '16px' }}
        >
          Submit Another Feedback
        </Button>
      </div>

      <div className="mt-3 text-gray-400 text-xs">
        <p>Thank you for using our feedback kiosk</p>
      </div>
    </div>
  );
};

export default FeedbackThankYou;